import { CheckIcon, CopyIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCopy } from "@/hooks/use-copy";
import { useOrganizationStore } from "@/store/organization";

interface AccountIdCardProps {
  variant?: "compact" | "detailed";
}

/** Displays the active organization's API account ID with clipboard feedback. */
export function AccountIdCard({ variant = "detailed" }: AccountIdCardProps) {
  const accountId = useOrganizationStore(
    (state) => state.organization?.account_id,
  );
  const { copy, copied } = useCopy();

  const copyButton = (
    <Button
      variant="outline"
      size="sm"
      className="shrink-0"
      aria-label={copied ? "Account ID copied" : "Copy account ID"}
      onClick={() => accountId && copy(accountId)}
      disabled={!accountId}
    >
      {copied ? (
        <>
          <CheckIcon className="mr-2 size-4" />
          Copied
        </>
      ) : (
        <>
          <CopyIcon className="mr-2 size-4" />
          Copy
        </>
      )}
    </Button>
  );

  if (variant === "detailed") {
    return (
      <Card>
        <CardHeader className="border-b">
          <CardTitle>Account ID</CardTitle>
        </CardHeader>
        <CardContent className="px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <p className="text-[13px] text-muted-foreground">
                This is the account ID used for API calls
              </p>
              <p className="break-all font-medium">
                {accountId || "No account ID available"}
              </p>
            </div>
            {copyButton}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="py-0">
      <CardContent className="flex min-h-20 items-center gap-4 px-6 py-4">
        <CardTitle className="shrink-0">Account ID</CardTitle>
        <span
          className="min-w-0 flex-1 truncate font-mono text-sm text-muted-foreground"
          title={accountId}
        >
          {accountId || "No account ID available"}
        </span>
        {copyButton}
      </CardContent>
    </Card>
  );
}
